import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  LogOut,
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    totalRevenue: 0,
    weekRevenue: 0
  });

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    fetchBookings();
  }, []);

  useEffect(() => {
    // Filter bookings when filter changes
    if (statusFilter === 'all') {
      setFilteredBookings(bookings);
    } else {
      setFilteredBookings(bookings.filter(b => b.status === statusFilter));
    }
  }, [statusFilter, bookings]);

  useEffect(() => {
    // Calculate stats
    if (bookings.length > 0) {
      const pending = bookings.filter(b => b.status === 'pending').length;
      const confirmed = bookings.filter(b => b.status === 'confirmed').length;
      const completed = bookings.filter(b => b.status === 'completed').length;
      
      const totalRevenue = bookings
        .filter(b => b.status === 'completed')
        .reduce((sum, b) => sum + b.estimatedPrice, 0);
      
      // Calculate this week's revenue (simplified - last 7 days)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekRevenue = bookings
        .filter(b => b.status === 'completed' && new Date(b.createdAt) > weekAgo)
        .reduce((sum, b) => sum + b.estimatedPrice, 0);

      setStats({
        total: bookings.length,
        pending,
        confirmed,
        completed,
        totalRevenue,
        weekRevenue
      });
    }
  }, [bookings]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertCircle },
      confirmed: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircle },
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
    };
    
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    
    return (
      <Badge className={`${variant.bg} ${variant.text} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status: newStatus });
      // Refresh bookings after update
      await fetchBookings();
      // Update selected booking
      setSelectedBooking(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking status. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Hill Boyz Admin Dashboard</h1>
            <p className="text-amber-100 text-sm">Manage your RV detailing bookings</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="bg-white text-orange-600 hover:bg-amber-50 border-0"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">This Week</p>
                  <p className="text-3xl font-bold text-green-600">${stats.weekRevenue}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold text-orange-600">${stats.totalRevenue}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Bookings</CardTitle>
                <CardDescription>Manage your customer bookings</CardDescription>
              </div>
              <div className="flex gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bookings</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={fetchBookings} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600">Loading bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-600 text-lg font-medium mb-2">No bookings yet</p>
                <p className="text-gray-500 text-sm">New bookings will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">RV Details</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date & Time</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Price</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-semibold text-gray-900">{booking.customerName}</p>
                            <p className="text-sm text-gray-600">{booking.customerPhone}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{booking.rvType}</p>
                            <p className="text-sm text-gray-600">{booking.rvLength} ft</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-gray-900">
                              {booking.preferredDate ? format(new Date(booking.preferredDate), 'MMM d, yyyy') : 'TBD'}
                            </p>
                            <p className="text-sm text-gray-600">{booking.preferredTime || 'Any time'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-bold text-orange-600 text-lg">${booking.estimatedPrice}</p>
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(booking.status)}
                        </td>
                        <td className="px-4 py-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedBooking(booking)}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Booking Details Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Booking Details</DialogTitle>
            <DialogDescription>
              Booking ID: {selectedBooking?.id}
            </DialogDescription>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-600" />
                  Customer Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p><strong>Name:</strong> {selectedBooking.customerName}</p>
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-600" />
                    <a href={`tel:${selectedBooking.customerPhone}`} className="text-blue-600 hover:underline">
                      {selectedBooking.customerPhone}
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-600" />
                    <a href={`mailto:${selectedBooking.customerEmail}`} className="text-blue-600 hover:underline">
                      {selectedBooking.customerEmail}
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-600" />
                    {selectedBooking.serviceAddress}
                  </p>
                </div>
              </div>

              {/* Service Details */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Service Details</h3>
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 space-y-2">
                  <p><strong>RV Type:</strong> {selectedBooking.rvType}</p>
                  <p><strong>RV Length:</strong> {selectedBooking.rvLength} ft</p>
                  <p><strong>Estimated Price:</strong> <span className="text-2xl font-bold text-orange-600">${selectedBooking.estimatedPrice}</span></p>
                </div>
              </div>

              {/* Schedule */}
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  Schedule
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p><strong>Preferred Date:</strong> {selectedBooking.preferredDate ? format(new Date(selectedBooking.preferredDate), 'MMMM d, yyyy') : 'TBD'}</p>
                  <p><strong>Preferred Time:</strong> {selectedBooking.preferredTime || 'Any time'}</p>
                  <p className="text-sm text-gray-600">
                    <strong>Booked:</strong> {format(new Date(selectedBooking.createdAt), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              </div>

              {/* Special Requests */}
              {selectedBooking.specialRequests && (
                <div>
                  <h3 className="font-semibold text-lg mb-3">Special Requests</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">{selectedBooking.specialRequests}</p>
                  </div>
                </div>
              )}

              {/* Status Management */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Update Status</h3>
                <div className="flex gap-2">
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'pending')}
                    variant={selectedBooking.status === 'pending' ? 'default' : 'outline'}
                    className={selectedBooking.status === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                  >
                    Pending
                  </Button>
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'confirmed')}
                    variant={selectedBooking.status === 'confirmed' ? 'default' : 'outline'}
                    className={selectedBooking.status === 'confirmed' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  >
                    Confirmed
                  </Button>
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'completed')}
                    variant={selectedBooking.status === 'completed' ? 'default' : 'outline'}
                    className={selectedBooking.status === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    Completed
                  </Button>
                  <Button
                    onClick={() => updateBookingStatus(selectedBooking.id, 'cancelled')}
                    variant={selectedBooking.status === 'cancelled' ? 'default' : 'outline'}
                    className={selectedBooking.status === 'cancelled' ? 'bg-red-600 hover:bg-red-700' : ''}
                  >
                    Cancelled
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
